import { computed } from 'vue'
import { useAnalytics } from '@/composables/useAnalytics'
import { useSourceTokens } from '@/features/crosschain-buy/source-tokens'
import { useQuotes } from '@/features/crosschain-buy/quotes'
import { getBuyFlowController } from '@/features/crosschain-buy/buy-flow/controller'
import { useWallets } from '@/features/crosschain-buy/wallets/use-wallets'
import { useRouteUxStatus } from './useRouteUxStatus'
import { useCrosschainModalLifecycle } from './useCrosschainModalLifecycle'

export function useCrosschainBuy() {
  const controller = getBuyFlowController()
  const quotes = useQuotes()
  const { trackBuySpotty } = useAnalytics()
  const sourceTokens = useSourceTokens()
  const wallets = useWallets()

  const {
    sourceChainOptions,
    selectedChainId,
    selectedTokenAddress: selectedSourceTokenAddress,
    amount: sourceAmount,
    chainType: sourceChainType,
    availableTokens: availableSourceTokens,
    isLoading: isLoadingTokens,
    blockedMessage: tokenLoadBlockedMessage,
    selectChain: selectSourceChain,
    selectToken: selectSourceToken,
    setAmount: setSourceAmount,
  } = sourceTokens

  const {
    isFresh: isQuoteFresh,
    hasPrerequisites: hasQuotePrerequisites,
    requote: triggerRequote,
    scheduleRequote: debouncedRequote,
    scheduleInitialQuote: debouncedAutoQuote,
    syncForSelection: syncQuoteForCurrentSelection,
  } = quotes

  const entryPoint = computed(() => controller.state.value.entryPoint)
  const isModalOpen = computed(() => controller.state.value.isModalOpen)

  const {
    closeModal,
    openFromCta,
    setStep,
    markBridgeCompleted,
    resetForNewSwap,
    invalidateRouteContext,
    runContinue,
    getModalPresentation,
  } = useCrosschainModalLifecycle({
    isQuoteFresh,
    hasQuotePrerequisites,
    triggerRequote,
    debouncedAutoQuote,
    syncQuoteForCurrentSelection,
    syncTonWalletFromSession: wallets.syncTonSession,
    trackBuySpotty,
  })

  const solanaBridgeProvider = computed({
    get: () => quotes.solanaBridgeProvider.value,
    set: (v: 'rango' | 'layerswap') => {
      quotes.selectSolanaBridgeProvider(v)
    },
  })

  const {
    currentStep,
    routeUxStatus,
    canCancelExecution,
    canAbandonExecution,
    routeLocked,
    networkSelectionLocked,
    cancelExecution,
    endExecution,
  } = useRouteUxStatus({ selectedSourceTokenAddress, sourceAmount, hasQuotePrerequisites })

  const state = computed(() => controller.state.value)

  return {
    entryPoint,
    isModalOpen,
    currentStep,
    sourceChainOptions,
    selectedChainId,
    selectedSourceTokenAddress,
    sourceAmount,
    sourceChainType,
    availableSourceTokens,
    isLoadingTokens,
    tokenLoadBlockedMessage,
    isQuoteFresh,
    routeUxStatus,
    state,

    openFromCta,
    closeModal,
    selectSourceChain,
    selectSourceToken,
    setSourceAmount,
    runContinue,
    resetForNewSwap,
    getModalPresentation,
    setStep,
    markBridgeCompleted,
    debouncedRequote,
    debouncedAutoQuote,
    triggerRequote,
    syncQuoteForCurrentSelection,
    solanaBridgeProvider,
    invalidateRouteContext,
    canCancelExecution,
    canAbandonExecution,
    routeLocked,
    networkSelectionLocked,
    cancelExecution,
    endExecution,
    abandonExecution: controller.endExecution,
  }
}
